'use client'

import displayError from '@/api/errors/errorResponse'
import { registerService } from '@/api/services/auth/authService'
import skillpassLogo from '@/assets/skillpass_v2.svg'
import { Checkbox } from '@/components/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/forms/form/Form'
import FormWrap from '@/components/forms/form/FormWrap'
import { Input } from '@/components/forms/inputs/text/Input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { displayToast } from '@/components/ui/toast/displayToast'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { z } from 'zod'

const basicFormSchema = z
  .object({
    email: z.string().email(),
    password: z
      .string()
      .min(12, 'Hasło musi mieć minimum 12 znaków')
      .max(48, 'Hasło nie może przekraczać 48 znaków')
      .regex(/[A-Z]/, 'Hasło musi zawierać przynajmniej jedną dużą literę')
      .regex(/[a-z]/, 'Hasło musi zawierać przynajmniej jedną małą literę')
      .regex(/[0-9]/, 'Hasło musi zawierać przynajmniej jedną cyfrę')
      .regex(/[^A-Za-z0-9]/, 'Hasło musi zawierać przynajmniej jeden znak specjalny'),
    confirmPassword: z
      .string()
      .min(12, 'Hasło musi mieć minimum 12 znaków')
      .max(48, 'Hasło nie może przekraczać 48 znaków')
      .regex(/[A-Z]/, 'Hasło musi zawierać przynajmniej jedną dużą literę')
      .regex(/[a-z]/, 'Hasło musi zawierać przynajmniej jedną małą literę')
      .regex(/[0-9]/, 'Hasło musi zawierać przynajmniej jedną cyfrę')
      .regex(/[^A-Za-z0-9]/, 'Hasło musi zawierać przynajmniej jeden znak specjalny')
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Hasła nie są identyczne',
    path: ['confirmPassword']
  })

const extendedFormSchema = z
  .object({
    email: z.string().email(),
    password: z
      .string()
      .min(12, 'Hasło musi mieć minimum 12 znaków')
      .max(48, 'Hasło nie może przekraczać 48 znaków')
      .regex(/[A-Z]/, 'Hasło musi zawierać przynajmniej jedną dużą literę')
      .regex(/[a-z]/, 'Hasło musi zawierać przynajmniej jedną małą literę')
      .regex(/[0-9]/, 'Hasło musi zawierać przynajmniej jedną cyfrę')
      .regex(/[^A-Za-z0-9]/, 'Hasło musi zawierać przynajmniej jeden znak specjalny'),
    confirmPassword: z
      .string()
      .min(12, 'Hasło musi mieć minimum 12 znaków')
      .max(48, 'Hasło nie może przekraczać 48 znaków')
      .regex(/[A-Z]/, 'Hasło musi zawierać przynajmniej jedną dużą literę')
      .regex(/[a-z]/, 'Hasło musi zawierać przynajmniej jedną małą literę')
      .regex(/[0-9]/, 'Hasło musi zawierać przynajmniej jedną cyfrę')
      .regex(/[^A-Za-z0-9]/, 'Hasło musi zawierać przynajmniej jeden znak specjalny'),
    firstName: z.string().min(2, 'Imię musi mieć minimum 2 znaki'),
    lastName: z.string().min(2, 'Nazwisko musi mieć minimum 2 znaki'),
    gender: z.enum(['MALE', 'FEMALE']),
    birthDate: z.string().min(1, 'Data urodzenia jest wymagana'),
    telephone: z.string().min(9, 'Numer telefonu musi mieć minimum 9 znaków').optional(),
    marketing: z.boolean().optional()
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Hasła nie są identyczne',
    path: ['confirmPassword']
  })

type BasicFormData = z.infer<typeof basicFormSchema>
type ExtendedFormData = z.infer<typeof extendedFormSchema>

export default function RegisterForm() {
  const [showAdditionalFields, setShowAdditionalFields] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isRegistered, setIsRegistered] = useState(false)

  const form = useForm<BasicFormData | ExtendedFormData>({
    resolver: zodResolver(showAdditionalFields ? extendedFormSchema : basicFormSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      gender: undefined,
      birthDate: '',
      telephone: undefined,
      marketing: false
    }
  })

  const onSubmit = async (data: BasicFormData | ExtendedFormData) => {
    if (!showAdditionalFields) {
      setShowAdditionalFields(true)
      return
    }

    setIsLoading(true)
    try {
      const extendedData = data as ExtendedFormData
      const res = await registerService({
        ...extendedData,
        telephone: extendedData.telephone ?? null,
        marketing: extendedData.marketing ?? false
      })

      if (!res.success) {
        displayError(res)
        return
      }

      setIsRegistered(true)
      displayToast('success', 'Sukces', 'Rejestracja zakończona pomyślnie')
    } catch (error) {
      displayToast('error', 'Błąd', 'Wystąpił nieoczekiwany błąd')
    } finally {
      setIsLoading(false)
    }
  }

  if (isRegistered) {
    return (
      <div className="flex min-h-screen">
        {/* Lewa strona z logo */}
        <div className="flex w-1/2 items-center justify-center bg-white p-8">
          <div className="w-full">
            <img src={skillpassLogo} alt="Skillpass Logo" className="h-auto w-full" />
          </div>
        </div>

        {/* Prawa strona z komunikatem */}
        <div className="flex w-1/2 items-center justify-center bg-gradient-to-br from-[#1d475f] to-[#2a5f7c] p-8">
          <div className="w-full max-w-md text-center">
            <h2 className="mb-4 text-2xl font-semibold text-white">
              Dziękujemy za rejestrację!
            </h2>
            <p className="mb-4 text-gray-200">
              Na podany adres email wysłaliśmy link aktywacyjny. Prosimy o potwierdzenie
              rejestracji poprzez kliknięcie w link w wiadomości.
            </p>
            <Link to="/login" className="font-medium text-white hover:text-white/90">
              Przejdź do logowania
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen">
      {/* Lewa strona z logo */}
      <div className="flex w-1/2 items-center justify-center bg-white p-8">
        <div className="w-full">
          <img src={skillpassLogo} alt="Skillpass Logo" className="h-auto w-full" />
        </div>
      </div>

      {/* Prawa strona z formularzem */}
      <div className="flex w-1/2 items-center justify-center bg-gradient-to-br from-[#1d475f] to-[#2a5f7c] p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="mb-2 text-2xl font-semibold text-white">
              Darmowa rejestracja
            </h1>
            <p className="text-sm text-gray-200">
              Utwórz swoje darmowe konto SkillPass już teraz.
            </p>
          </div>

          <Form {...form}>
            <FormWrap
              buttonClassName="w-full bg-white hover:bg-gray-100 text-[#1d475f] font-medium mt-6"
              onSubmit={form.handleSubmit(onSubmit)}
              submitText={showAdditionalFields ? 'Zarejestruj się' : 'Kontynuuj'}
              disabled={isLoading}>
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/10">
                  <Loader2 className="h-6 w-6 animate-spin text-white" />
                </div>
              )}
              <div className="flex w-full flex-col gap-5">
                {!showAdditionalFields && (
                  <>
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white">Email *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Wprowadź email"
                              type="email"
                              {...field}
                              required
                              className="border-white/20 bg-white/10 text-white placeholder:text-white/50"
                            />
                          </FormControl>
                          <FormMessage className="text-red-300" />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white">Hasło *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Wprowadź hasło"
                              type="password"
                              {...field}
                              required
                              className="border-white/20 bg-white/10 text-white placeholder:text-white/50"
                            />
                          </FormControl>
                          <FormMessage className="text-red-300" />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white">Potwierdź hasło *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Potwierdź hasło"
                              type="password"
                              {...field}
                              required
                              className="border-white/20 bg-white/10 text-white placeholder:text-white/50"
                            />
                          </FormControl>
                          <FormMessage className="text-red-300" />
                        </FormItem>
                      )}
                    />
                  </>
                )}

                {showAdditionalFields && (
                  <>
                    <div className="flex gap-4">
                      <FormField
                        control={form.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormLabel className="text-white">Imię *</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Wprowadź imię"
                                {...field}
                                required
                                className="border-white/20 bg-white/10 text-white placeholder:text-white/50"
                              />
                            </FormControl>
                            <FormMessage className="text-red-300" />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormLabel className="text-white">Nazwisko *</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Wprowadź nazwisko"
                                {...field}
                                required
                                className="border-white/20 bg-white/10 text-white placeholder:text-white/50"
                              />
                            </FormControl>
                            <FormMessage className="text-red-300" />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="gender"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white">Płeć *</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger className="border-white/20 bg-white/10 text-white">
                                <SelectValue placeholder="Wybierz płeć" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="bg-white">
                              <SelectItem value="FEMALE">Kobieta</SelectItem>
                              <SelectItem value="MALE">Mężczyzna</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage className="text-red-300" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="birthDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white">Data urodzenia *</FormLabel>
                          <FormControl>
                            <Input
                              type="date"
                              {...field}
                              required
                              className="border-white/20 bg-white/10 text-white placeholder:text-white/50"
                            />
                          </FormControl>
                          <FormMessage className="text-red-300" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="telephone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white">Telefon</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Wprowadź numer telefonu"
                              {...field}
                              className="border-white/20 bg-white/10 text-white placeholder:text-white/50"
                            />
                          </FormControl>
                          <FormMessage className="text-red-300" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="marketing"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-y-0 space-x-3">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              className="border-white/20"
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="text-sm text-white">
                              Wyrażam zgodę na otrzymywanie informacji marketingowych
                            </FormLabel>
                          </div>
                        </FormItem>
                      )}
                    />
                  </>
                )}
              </div>

              <div className="mt-6 text-center text-sm text-white/80">
                Poprzez rejestrację oświadczasz, że zapoznałeś się z{' '}
                <Link to="/terms" className="text-white hover:text-white/90">
                  Regulaminem
                </Link>
              </div>
            </FormWrap>
          </Form>

          <div className="mt-8 text-center">
            <span className="text-sm text-white/80">Masz już konto? </span>
            <Link
              to="/login"
              className="text-sm font-medium text-white hover:text-white/90">
              Zaloguj się
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
